import { z } from 'zod';
import {
  JsonValueSchema,
  SignalAssetTypeSchema,
  SignalCategorySchema,
  SignalOriginSchema,
  SignalRankSchema,
  SignalStageSchema,
  SignalStatusSchema,
  BlockedReasonSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  TargetTypeSchema,
} from '@terra/shared-schemas-orchestrator';
import { ExternalNetworkAttackScopeSchema } from '@terra/shared-contracts-external-network';
import { AssetLifecycleDtoSchema } from '@terra/shared-dto-types';
import { SignalSourceSchema } from './signal-source.util';

/**
 * Hand-rolled base for signal HTTP DTOs. See `BaseFindingDtoSchema` for
 * the rationale (decoupled wire shape, polymorphic `targetId`+`targetType`).
 */
export const BaseSignalDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  targetId: z.string(),
  targetType: TargetTypeSchema,
  category: SignalCategorySchema,
  cweIds: z.array(z.string()).default([]),
  owaspCategory: z.string().nullable(),
  origin: SignalOriginSchema,
  rank: SignalRankSchema,
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullable(),
  blockedReason: BlockedReasonSchema.nullable(),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.nullable(),
  endpointId: z.string().nullable(),
  harEntryId: z.string().nullable(),
  entryPointId: z.string().nullable(),
  securityTestCaseId: z.string().nullable(),
  securityTestCaseSummary: z.string().nullable(),
  description: z.string().nullable(),
  analysis: z.string().nullable(),
  confidence: z.int().nullable(),
  reason: z.string().nullable(),
  isAuthenticated: z.boolean().nullable(),
  smokeTestSummary: z.string().nullable(),
  lastSmokeTestedAt: z.coerce.date().nullable(),
  assigneeId: z.string().nullable(),
  isActive: z.boolean(),
  sensitive: z.boolean().default(false),
  prUrl: z.string().nullable(),
  validatedByCode: z.boolean().nullable(),
  validationRejected: z.boolean().nullable(),
  validationReasoning: z.string().nullable(),
  isAppWide: z.boolean().nullable(),
  metadata: JsonValueSchema.nullable(),
  sourceLabel: z.string().nullable(),
  sourceProvider: z.string().nullable(),
  importBatchId: z.string().nullable(),
  source: SignalSourceSchema,
  assetId: z.string().nullable(),
  assetType: SignalAssetTypeSchema.nullable(),
  attackScope: ExternalNetworkAttackScopeSchema.nullable(),
  dedupeFingerprint: z.string().nullable(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type BaseSignalDto = z.infer<typeof BaseSignalDtoSchema>;

/**
 * App-bounded variant — `applicationId` for web-application scoped routes.
 * Responses map from stored `targetId` / `targetType` in the orchestrator.
 */
export const BaseAppSignalDtoSchema = BaseSignalDtoSchema.omit({
  targetId: true,
  targetType: true,
}).extend({
  applicationId: z.string(),
  targetId: z.string().optional(),
  targetType: TargetTypeSchema.optional(),
});

export type BaseAppSignalDto = z.infer<typeof BaseAppSignalDtoSchema>;
